import React, { useMemo, useState } from 'react';
import { Select } from 'ALIASED-antd';
import type { SelectProps as AntdSelectProps } from 'ALIASED-antd';

interface BaseOptionType {
  disabled?: boolean;
  value: string | number;
  label: React.ReactNode;
}

interface DefaultOptionType extends BaseOptionType {
  children?: Omit<DefaultOptionType, 'children'>[];
}

type ArrayElementType<T> = T extends (infer E)[] ? E : T;

interface WrappedSelectProps<ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType>
  extends AntdSelectProps<ValueType, OptionType> {
  'data-testid': string;
}

const mergePropOptions = (options: WrappedSelectProps<any, BaseOptionType>['options']): BaseOptionType[] => {
  if (!options) return [];

  return options.flatMap((option) => {
    if ('options' in option && Array.isArray(option.options)) {
      return option.options;
    }
    return option;
  });
};

const mergeChildrenOptions = <OptionType extends BaseOptionType | DefaultOptionType>(
  children: React.ReactNode
): OptionType[] => {
  if (!children) return [];

  const processChild = (child: React.ReactNode): OptionType[] => {
    if (!React.isValidElement(child)) return [];

    const { type, props } = child;

    if (type === Select.Option) {
      const optionProps = props as { value: string | number; children: React.ReactNode };
      return [{ value: optionProps.value, label: optionProps.children as string }] as OptionType[];
    }

    if (type === Select.OptGroup) {
      const optGroupProps = props as { children: React.ReactNode };
      const { children: optGroupChildren } = optGroupProps;

      if (!optGroupChildren) return [];

      const childrenArray = Array.isArray(optGroupChildren) ? optGroupChildren : [optGroupChildren];
      return childrenArray.flatMap(processChild);
    }

    return [];
  };

  const childrenArray = Array.isArray(children) ? children : [children];
  return childrenArray.flatMap(processChild);
};

export const WrappedAntdSelect = <
  ValueType = string | number,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>(
  props: WrappedSelectProps<ValueType, OptionType>
) => {
  const [internalValue, setInternalValue] = useState<ValueType>();

  const mergedOptions = useMemo(() => {
    if (props.options) {
      // extract and merge options from options props passed in form of array of options and option groups
      return mergePropOptions(props.options);
    }

    if (props.children) {
      // Extract and merge options from Option and OptGroup components if children are provided
      return mergeChildrenOptions(props.children);
    }

    return [];
  }, [props.children, props.options]);

  const simulateTestClick = (value: ValueType, option: OptionType) => {
    return () => {
      if (!props.value && !props.onChange && !props.onSelect) {
        setInternalValue(value);
      }
      props?.onChange?.(value, option);
      props?.onSelect?.(value as ArrayElementType<ValueType>, option);
    };
  };

  const valueSource = useMemo(() => {
    return props.value !== undefined ? props.value : internalValue;
  }, [props.value, internalValue]);

  return (
    <>
      <Select {...props} value={valueSource} style={{ width: '100%', ...props.style }} />
      {/* Hidden markup for automation tests */}
      <div data-select-value-map={props['data-testid']} className="d-none">
        {mergedOptions?.map((option) => (
          <div
            key={option.value}
            data-value={option.value}
            data-label={option.label}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                simulateTestClick(option.value as ValueType, option as OptionType);
              }
            }}
            onClick={simulateTestClick(option.value as ValueType, option as OptionType)}
            role="button"
          />
        ))}
      </div>
    </>
  );
};

WrappedAntdSelect.Option = Select.Option as unknown as React.FC<any>;
WrappedAntdSelect.OptGroup = Select.OptGroup as unknown as React.FC<any>;
